import { gql } from '@apollo/client';

const BlendedComputationDetails = gql`
  fragment BlendedComputationDetails on Margins {
    blended
    blendedComputation {
      newGlp
      previousGlp
      upsellPercentage
      renewPercentage
      upsellValue
      renewalValue
      upsellMargin
      renewalMargin
      blendedMargin
    }
  }
`;

const QuoteFragment = gql`
  fragment QuoteFragment on Quote {
    reestimationInProgress
    id
    quoteNumber
    name
    status
    billingGroupId
    version
    quoteContact
    contractType
    transactionAccountId
    pricingCurrency @include(if: $isMultiCurrencyChangesEnabled)
    invoiceGroup {
      name
      purchaseOrder {
        number
      }
      pricingEligibility
    }
    externalNotes {
      note
    }
    expiresAfterDays
    billTo {
      companyName
      addressLine1
      addressLine2
      city
      state
      country
      postalCode
      taxIds {
        label
        taxIdLabel
        taxExempt
        taxIdDescription
        id
      }
    }
    shipTo {
      id
      companyName
      addressLine1
      addressLine2
      city
      state
      country
      postalCode
      taxIds {
        label
        taxIdLabel
        taxExempt
        taxIdDescription
        id
      }
    }
    adminFee
    adminFeeSubTotal
    discount
    subTotal
    total
    taxEstimate
    revision {
      totalCount
    }
    quoteAuthor
    revisionNumber
    currency
    expiryDate
    createdDate
    updatedDate
    estimateComputedAt
    updatedBy
    upgradeCredits
    staleReason {
      code
      name
      lastUpdatedAt
      expiresAt
      orderId
      orderItemId
    }
    cancelledReason {
      code
      name
      lastUpdatedAt
      orderId
      orderItemId
    }
    lineItems {
      metadata {
        legacyInvoiceId
        cloudSiteHostname
        cloudSiteId
        orgName
        orgId
      }
      status
      lineItemId
      accountName
      description
      orgId
      orgName
      linkedOrgName
      billingAnchor {
        type
        timestamp
      }
      startsAt {
        type
        timestamp
      }
      margins {
        amount
        type
        percent
        startsAt
        endsAt
        total
        taxAmount
        ...BlendedComputationDetails
      }
      marginTotal
      billingAnchorDate
      cloudSiteId
      cloudSiteHostname
      entitlementId
      entitlementNumber
      entitlementTransactionAccountId
      productKey
      logoId
      offeringKey
      pricingPlanId
      pricingCurrency @include(if: $isMultiCurrencyChangesEnabled)
      entitlementVersion
      lineItemType
      quantity
      startDate
      endDate
      total
      subTotal
      taxEstimate
      billingFrequency
      endsAt {
        type
        timestamp
        duration {
          interval
          intervalCount
        }
      }
      entitlement @include(if: $isFetchingEntitlementEnabled) {
        billingStatus
        billingGroupId
        pauseBilling {
          startTimestamp
          endTimestamp
        }
        scheduledChanges {
          pauseBilling {
            startTimestamp
            endTimestamp
          }
        }
        offering {
          id
          name
          description
          productKey
          relationshipRequirements {
            side
            level
            type
          }
          product {
            id
            name
          }
        }
        relatesToEntitlements {
          relationshipType
        }
      }
      proratedCharges @include(if: $isProratedChangesEnabled) {
        subTotal
        credits
        discountTotal
        total
        users
        otherDiscount
        partnerDiscountDetails {
          totalPartnerDiscount
          partnerDiscounts {
            promotionId
            discountAmount
            percentOff
            reasonCode
            discountType
            period {
              startsAt
              endsAt
            }
          }
        }
        taxEstimate
        taxItems {
          tax
          taxAmountLabel
          taxPercent
        }
      }
      preBillingConfiguration {
        billFrom {
          type
          timestamp
        }
        billTo {
          type
          timestamp
          duration {
            interval
            intervalCount
          }
        }
      }
      relatesFromEntitlements {
        relationshipType
        lineItemId
        referenceType
        entitlementId
      }
      pricingPlan {
        pricingPlanType
        billingFrequency
      }
      userTierLabel
      chargeQuantities {
        quantity
        chargeElement
      }
      offerings {
        offeringName
        pricingType
      }
      offering {
        relationshipRequirements {
          side
          type
          level
        }
      }
      promotions {
        promotionInstanceId
        type
        promotionDefinition {
          customisedValues {
            benefits {
              duration
              value
              values {
                appliedOn
                value
              }
              iterations
            }
            applicationReason {
              id
            }
          }
          promotionId
          promotionCode
        }
      }
      promotionDetails {
        id
        promotionCode
        title
        type
        benefits {
          benefitType
          duration
          subBenefitType
          iterations
          value
          scale
          type
        }
        promotionInstanceId
        promotionWindow {
          endTime
          startTime
        }
        status
        cycleInterval
        pricingType
        eligibilityRules {
          products
          cycleInterval
          pricingType
          partnerType
          partnerTier
          saleTransitionType
          currency
        }
        cycleIntervalWithEligibleConditions
        applicationReason {
          id
          readableName
          customisable {
            id {
              type
              anyOf {
                readableName
                id
              }
            }
          }
        }
        customisableReasons {
          readableName
          id
        }
        isPromotionCustomisable
      }
      promotionStatus
      discountTotal
      discountDetails {
        promotionId
        discountAmount
        percentOff
        reasonCode
        discountType
      }
      taxItems {
        tax
        taxAmountLabel
        taxPercent
      }
      upgradeCredits
      upgradeCreditDetails {
        subTotal
        startsAt
        endsAt
        taxItems {
          tax
          taxAmountLabel
          taxPercent
        }
        total
        margins {
          promotionId
          amount
          percent
          reasonCode
          type
        }
        invoiceRequestItemIds
      }
      staleReason {
        code
        name
        lastUpdatedAt
        expiresAt
        orderId
        orderItemId
      }
      cancelledReason {
        code
        name
        lastUpdatedAt
        orderId
        orderItemId
      }
      adminFee
      pricingCurrency
    }
    metadata {
      ecc_aaids
      quoteContact
      contractType
      contractId
    }
  }
`;
export const fetchQuoteDetailsPPC = gql`
  ${BlendedComputationDetails}
  ${QuoteFragment}
  query fetchQuoteDetailsPPC(
    $id: String
    $txaId: String
    $isProratedChangesEnabled: Boolean = false
    $isMultiCurrencyChangesEnabled: Boolean = false
    $isFetchingEntitlementEnabled: Boolean = false
  ) {
    quote(id: $id, txaId: $txaId) {
      ...QuoteFragment
    }
  }
`;

export const fetchQuoteV2DetailsPPC = gql`
  ${BlendedComputationDetails}
  ${QuoteFragment}
  query fetchQuoteDetailsPPC(
    $id: String
    $txaId: String
    $isProratedChangesEnabled: Boolean = false
    $isMultiCurrencyChangesEnabled: Boolean = false
    $isFetchingEntitlementEnabled: Boolean = false
  ) {
    quote: quoteV2(id: $id, txaId: $txaId) {
      ...QuoteFragment
    }
  }
`;
